import React, { useState } from 'react';
import { IonAvatar } from '@ionic/react';
import { generateInitials, generateMonogramColor, getViewableProfilePictureUrl } from '../utils/avatarUtils';

interface AvatarProps {
  user: {
    id: string;
    full_name?: string;
    email?: string;
    profile_picture_url?: string;
    profile_picture_visibility?: 'public' | 'private' | 'none';
    avatar_url?: string; // Legacy field for backwards compatibility
  };
  currentUserId?: string;
  relationship?: 'coach' | 'coachee' | null;
  isAdmin?: boolean; // Admin can see private pictures but not hidden ones
  size?: 'small' | 'medium' | 'large' | 'extra-large';
  className?: string;
  showMonogramWhenNoImage?: boolean; // If false, will show empty avatar when no image
}

const Avatar: React.FC<AvatarProps> = ({
  user,
  currentUserId,
  relationship,
  isAdmin = false,
  size = 'medium',
  className = '',
  showMonogramWhenNoImage = true
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now()); // Force refresh when URL changes

  // Get the viewable profile picture URL based on privacy settings
  // Fall back to legacy avatar_url if no profile_picture_url
  const pictureUrl = user.profile_picture_url || user.avatar_url;
  
  // For the user's own profile picture, always show it regardless of privacy setting
  // (except when visibility is explicitly 'none')
  let profilePictureUrl: string | null = null;
  
  if (pictureUrl) {
    if (currentUserId === user.id) {
      // User viewing their own picture - always show their own picture
      // (they can see it even if visibility is 'none' for others)
      profilePictureUrl = pictureUrl;
    } else if (isAdmin) {
      // Admin viewing - can see private pictures but respects 'none' (hidden)
      profilePictureUrl = user.profile_picture_visibility === 'none' ? null : pictureUrl;
    } else {
      // Other user viewing - apply privacy rules (hide if 'none')
      profilePictureUrl = user.profile_picture_visibility === 'none' ? null : getViewableProfilePictureUrl(
        pictureUrl,
        user.profile_picture_visibility,
        user.id,
        currentUserId,
        relationship
      );
    }
  }



  // Reset image error and update key when URL changes
  React.useEffect(() => {
    setImageError(false);
    setImageKey(Date.now());
  }, [profilePictureUrl]);

  // Generate monogram data
  const initials = generateInitials(user.full_name || '', user.email);
  const monogramColor = generateMonogramColor(user.id);

  // Size classes
  const sizeClasses = {
    small: 'w-8 h-8 text-xs',
    medium: 'w-12 h-12 text-sm',
    large: 'w-16 h-16 text-base',
    'extra-large': 'w-24 h-24 text-xl'
  };

  const containerClasses = `${sizeClasses[size]} ${className}`;

  // Show image if available and no error
  if (profilePictureUrl && !imageError) {
    return (
      <IonAvatar className={containerClasses}>
        <img
          key={imageKey}
          src={profilePictureUrl}
          alt={user.full_name || user.email || 'User avatar'}
          onError={() => {
            setImageError(true);
          }}
          className="w-full h-full object-cover"
          crossOrigin="anonymous"
        />
      </IonAvatar>
    );
  }

  // Show monogram if enabled
  if (showMonogramWhenNoImage) {
    return (
      <div
        className={`${containerClasses} rounded-full flex items-center justify-center font-semibold text-white`}
        style={{ backgroundColor: monogramColor }}
      >
        {initials}
      </div>
    );
  }

  // Show empty avatar
  return (
    <IonAvatar className={containerClasses}>
      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
        <span className="text-gray-500 text-lg">👤</span>
      </div>
    </IonAvatar>
  );
};

export default Avatar; 